/**
 * Guardarraíles: los límites que el cliente puede mover sin redespliegue.
 *
 * Los rangos, las etiquetas y las explicaciones vienen del backend, no de aquí. Es
 * el mismo diccionario que valida el PUT, así que este formulario no puede aceptar
 * un valor que el servidor vaya a rechazar ni quedarse desfasado cuando un rango
 * cambie.
 *
 * Cada campo dice si es un valor propio o el que trae el despliegue, y se puede
 * devolver al de fábrica uno por uno. Eso importa más de lo que parece: `null` en
 * la base de datos significa «hereda», así que restaurar un campo lo deja siguiendo
 * las futuras versiones en lugar de congelarlo en el número de hoy.
 *
 * Y hay una sección de lo que **no** se puede tocar, con el motivo. Un administrador
 * que venga buscando un selector de modelo debería enterarse aquí de que no existe y
 * de por qué, en lugar de suponer que se olvidó.
 */

import { useCallback, useEffect, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";

import { ApiError, api } from "../api/client";
import { ConfirmDialog } from "../components/ConfirmDialog";
import type { Policy, PolicyField } from "../api/types";
import "./admin.css";

/** Orden de presentación: de lo que más cambia la respuesta a lo más operativo. */
const ORDER = [
  "min_score_ratio",
  "default_chunks",
  "max_output_tokens",
  "history_turns",
  "max_upload_bytes",
];

export function Guardrails() {
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [draft, setDraft] = useState<Record<string, number | null>>({});
  const [level, setLevel] = useState<number | null>(null);
  const [prompt, setPrompt] = useState("");
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const fresh = await api.adminPolicy();
    setPolicy(fresh);
    setLevel(fresh.level);
    // El textarea arranca con el prompt en vigor, sea del nivel o propio, para que
    // editarlo sea partir de lo que hay y no de una hoja en blanco.
    setPrompt(fresh.prompt.text);
    setEditingPrompt(fresh.prompt.source === "organizacion");
    setDraft(
      Object.fromEntries(
        Object.entries(fresh.fields).map(([name, field]) => [
          name,
          field.overridden ? field.value : null,
        ]),
      ),
    );
  }, []);

  /** Field name to the label the panel shows, so an error can name a control. */
  const labels = Object.fromEntries(
    Object.entries(policy?.fields ?? {}).map(([field, spec]) => [field, spec.label]),
  );

  useEffect(() => {
    load().catch((e: unknown) => setError(describe(e)));
  }, [load]);

  async function save() {
    setSaving(true);
    setNotice(null);
    setError(null);
    try {
      await api.adminSetPolicy({
        ...draft,
        level,
        // Sin edición propia se manda null: eso significa «usa el prompt del
        // nivel», no «prompt vacío».
        prompt_override: editingPrompt ? prompt : null,
      });
      await load();
      setNotice("Guardado. Se aplica en la siguiente pregunta.");
    } catch (e: unknown) {
      setError(describe(e, labels));
    } finally {
      setSaving(false);
    }
  }

  /** Elegir un nivel mueve los cuatro deslizadores a la vez. */
  function pickLevel(next: number) {
    if (!policy) return;
    const preset = policy.levels.find((l) => l.level === next)?.preset ?? {};
    setLevel(next);
    setDraft((current) => ({ ...current, ...preset }));
    // El prompt del nivel se resuelve en el backend; el textarea se refresca al
    // recargar tras guardar. No se toca aquí para no pisar una edición en curso.
  }

  /** Mover un deslizador a mano saca de la escala: ya no es un nivel. */
  function setNumber(name: string, value: number) {
    setLevel(null);
    setDraft((current) => ({ ...current, [name]: value }));
  }

  if (error && !policy) {
    return (
      <div className="adm">
        <header className="page-head">
          <h1 className="page-title">Guardarraíles</h1>
        </header>
        <div className="page-body">
          <p className="adm-error">{error}</p>
        </div>
      </div>
    );
  }

  const numbersDirty =
    policy !== null &&
    ORDER.some((name) => {
      const field = policy.fields[name];
      if (!field) return false;
      const current = field.overridden ? field.value : null;
      return draft[name] !== current;
    });
  const promptDirty =
    policy !== null &&
    (editingPrompt !== (policy.prompt.source === "organizacion") ||
      (editingPrompt && prompt !== policy.prompt.text));
  const dirty = numbersDirty || promptDirty || (policy !== null && level !== policy.level);

  return (
    <div className="adm">
      <header className="page-head">
        <h1 className="page-title">Guardarraíles</h1>
        <AdminTabs />
        <div className="adm-save">
          {dirty && <span className="adm-dirty">Sin guardar</span>}
          <button
            type="button"
            className="btn btn--primary"
            disabled={saving || !dirty}
            onClick={() => void save()}
          >
            {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </header>

      <div className="page-body adm-body">
        <p className="page-sub">
          Estos límites se aplican en la siguiente pregunta, sin desplegar nada.
          Los rangos los impone el servidor.
        </p>

        {notice && <p className="adm-notice">{notice}</p>}
        {error && <p className="adm-error">{error}</p>}

        {policy === null ? (
          <p className="adm-empty">Cargando…</p>
        ) : (
          <>
            <LiveStrip policy={policy} />

            <section className="card" aria-label="Nivel de guardarraíl">
              <div className="adm-card-head">
                <h2 className="adm-card-title">Nivel</h2>
                <span className="adm-badge">{badge(policy, level)}</span>
              </div>
              <p className="adm-empty">
                Un nivel fija el prompt del asistente y los cuatro límites de abajo.
                Mover un límite a mano sale de la escala y pasa a «Personalizado».
              </p>
              <ul className="adm-levels">
                {policy.levels.map((option) => (
                  <li key={option.level}>
                    <button
                      type="button"
                      className={`adm-level${level === option.level ? " is-active" : ""}`}
                      onClick={() => pickLevel(option.level)}
                      aria-pressed={level === option.level}
                    >
                      <span className="adm-level-head">
                        <span className="adm-level-num">{option.level}</span>
                        <strong>{option.name}</strong>
                        {option.is_shipped_default && (
                          <span className="adm-tag adm-tag--muted">del despliegue</span>
                        )}
                      </span>
                      <span className="adm-level-summary">{option.summary}</span>
                      <span className="adm-level-use">{option.use_for}</span>
                      <span className="adm-level-preset">
                        {Object.entries(option.preset)
                          .map(([field, value]) => `${short(field)} ${format(field, value)}`)
                          .join(" · ")}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>

            <section className="card" aria-label="Instrucciones del asistente">
              <div className="adm-card-head">
                <h2 className="adm-card-title">Instrucciones del asistente</h2>
                <span className="adm-badge">{SOURCE_LABEL[policy.prompt.source]}</span>
              </div>

              <p className="adm-knob-help">{policy.prompt.note}</p>

              <label className="adm-toggle adm-toggle--block">
                <input
                  type="checkbox"
                  checked={editingPrompt}
                  onChange={(event) => {
                    setEditingPrompt(event.target.checked);
                    if (!event.target.checked && policy.prompt.level_text) {
                      setPrompt(policy.prompt.level_text);
                    }
                  }}
                />
                <span>Escribir instrucciones propias en lugar de las del nivel</span>
              </label>

              <textarea
                className="adm-prompt"
                value={prompt}
                readOnly={!editingPrompt}
                spellCheck={false}
                rows={16}
                maxLength={policy.prompt.max_chars}
                aria-label="Instrucciones del asistente"
                onChange={(event) => setPrompt(event.target.value)}
              />
              <div className="adm-knob-foot">
                <span className="adm-tag adm-tag--muted">
                  {prompt.length} / {policy.prompt.max_chars} caracteres
                </span>
                {editingPrompt && policy.prompt.level_text && (
                  <button
                    type="button"
                    className="btn btn--quiet"
                    onClick={() => setPrompt(policy.prompt.level_text ?? "")}
                  >
                    Volver al texto del nivel
                  </button>
                )}
                {/* Guardado propio de las instrucciones, con confirmación. Es el
                    único campo del panel que es texto libre y el que más cuesta
                    deshacer si se toca sin querer: un deslizador se vuelve a poner
                    de memoria, un prompt reescrito no. */}
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={!promptDirty || saving}
                  onClick={() => setConfirming(true)}
                >
                  Guardar instrucciones
                </button>
              </div>
            </section>

            {confirming && (
              <ConfirmDialog
                title="Cambiar las instrucciones del asistente"
                confirmLabel="Guardar instrucciones"
                busy={saving}
                onCancel={() => setConfirming(false)}
                onConfirm={() => {
                  setConfirming(false);
                  void save();
                }}
              >
                <p>
                  {editingPrompt
                    ? "El asistente pasará a usar estas instrucciones en lugar de las del nivel, desde la siguiente pregunta."
                    : "El asistente volverá a las instrucciones del nivel y se descartarán las propias."}
                </p>
                {/* `PUT /admin/policy` reemplaza la política entera, así que este
                    botón no puede guardar «solo» el prompt. En lugar de aparentar
                    que sí, se dice aquí qué más viaja en la misma escritura. */}
                {(numbersDirty || level !== policy.level) && (
                  <p>
                    Se guardan también los cambios pendientes de los límites y del
                    nivel: se escriben en la misma operación.
                  </p>
                )}
              </ConfirmDialog>
            )}

            <section className="card adm-knobs" aria-label="Límites ajustables">
              {ORDER.map((name) => [name, policy.fields[name]] as const)
                .filter(
                  (pair): pair is [string, PolicyField] =>
                    pair[1] !== undefined,
                )
                .map(([name, field]) => {
                  const value = draft[name] ?? field.default;
                  const isOverride = draft[name] !== null;
                  return (
                    <div key={name} className="adm-knob">
                      <div className="adm-knob-head">
                        <label
                          className="adm-knob-label"
                          htmlFor={`knob-${name}`}
                        >
                          {field.label}
                        </label>
                        <span className="adm-knob-value">
                          {format(name, value)}
                          {field.unit && name !== "max_upload_bytes" && (
                            <small> {field.unit}</small>
                          )}
                        </span>
                      </div>

                      <input
                        id={`knob-${name}`}
                        type="range"
                        className="adm-knob-range"
                        min={field.min}
                        max={field.max}
                        step={field.step}
                        value={value}
                        onChange={(event) =>
                          setNumber(name, Number(event.target.value))
                        }
                      />

                      <p className="adm-knob-help">{field.help}</p>

                      <div className="adm-knob-foot">
                        <span
                          className={
                            isOverride ? "adm-tag" : "adm-tag adm-tag--muted"
                          }
                        >
                          {isOverride
                            ? `Valor propio · el del despliegue es ${format(name, field.default)}`
                            : "Valor del despliegue"}
                        </span>
                        {isOverride && (
                          <button
                            type="button"
                            className="btn btn--quiet"
                            onClick={() =>
                              setDraft((current) => ({
                                ...current,
                                [name]: null,
                              }))
                            }
                            title="Vuelve a seguir el valor del despliegue, también en futuras versiones"
                          >
                            Restaurar
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
            </section>

            <section className="card" aria-label="No configurable">
              <div className="adm-card-head">
                <h2 className="adm-card-title">
                  Lo que no se puede cambiar aquí
                </h2>
              </div>
              <ul className="adm-fixed">
                {policy.not_tunable.map((item) => (
                  <li key={item.label} className="adm-fixed-row">
                    <span className="adm-fixed-label">{item.label}</span>
                    <span className="adm-fixed-value">{item.value}</span>
                    <span className="adm-fixed-why">{item.why}</span>
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function AdminTabs() {
  const navigate = useNavigate();
  const onGuardrails = useMatch("/administracion/guardarrailes") !== null;
  return (
    <nav className="adm-tabs" aria-label="Secciones del panel">
      <button
        type="button"
        className={`adm-tab${onGuardrails ? "" : " is-active"}`}
        onClick={() => navigate("/administracion")}
      >
        Monitorización
      </button>
      <button
        type="button"
        className={`adm-tab${onGuardrails ? " is-active" : ""}`}
        onClick={() => navigate("/administracion/guardarrailes")}
      >
        Guardarraíles
      </button>
    </nav>
  );
}

const SOURCE_LABEL: Record<string, string> = {
  organizacion: "Instrucciones propias",
  nivel: "Del nivel elegido",
  despliegue: "Las del despliegue",
};

/** Etiqueta corta para el resumen del preset de cada nivel. */
function short(field: string): string {
  return (
    {
      min_score_ratio: "exigencia",
      default_chunks: "fragmentos",
      max_output_tokens: "tokens",
      history_turns: "memoria",
    }[field] ?? field
  );
}

/**
 * Tira de estado: qué número está en vigor AHORA y de dónde sale.
 *
 * Existe porque la pantalla no sabía responder la pregunta más básica que trae
 * aquí a un administrador: «¿en qué está esto?». Había que leerlo de cinco
 * deslizadores, y cada uno se comparaba contra el valor del despliegue y no
 * contra el del nivel elegido — así que en nivel 5 el pie decía «el del
 * despliegue es 4» junto a un 8 perfectamente correcto.
 *
 * Tres estados por número, y la distinción es el motivo de todo esto:
 *
 *   «del nivel»      coincide con el preset del nivel elegido. Cambiar de nivel
 *                    lo moverá.
 *   «fuera del nivel» hay un nivel elegido y este número no es el suyo. Es el
 *                    aviso que faltaba: elegir un nivel lo va a pisar.
 *   «a medida»       no hay nivel; los cinco se llevan a mano.
 *
 * Lee de `policy`, no del borrador: es lo que está sirviendo respuestas, no lo
 * que hay en pantalla sin guardar. Los cambios sin guardar ya los señala la
 * cabecera, y mezclar las dos cosas haría que la tira mintiera justo cuando más
 * se la mira.
 */
export function LiveStrip({ policy }: { policy: Policy }) {
  const preset =
    policy.level === null
      ? null
      : (policy.levels.find((l) => l.level === policy.level)?.preset ?? null);

  return (
    <section className="adm-strip" aria-label="Valores en vigor">
      {ORDER.map((name) => [name, policy.fields[name]] as const)
        .filter((pair): pair is [string, PolicyField] => pair[1] !== undefined)
        .map(([name, field]) => {
          // `max_upload_bytes` no forma parte de ningún preset a propósito: es una
          // restricción operativa, no de calidad de respuesta. Decir de él que
          // está «fuera del nivel» sería falso. Se distingue por `undefined` y no
          // por `name in preset`, que no estrecha el tipo bajo
          // `noUncheckedIndexedAccess` y obligaba a un `!` justo donde el valor
          // puede faltar de verdad.
          const target = preset?.[name];
          const origin =
            target === undefined
              ? { text: "a medida", drift: false }
              : target === field.value
                ? { text: `del nivel ${policy.level}`, drift: false }
                : {
                    text: `fuera del nivel · el ${policy.level} pondría ${format(name, target)}`,
                    drift: true,
                  };

          return (
            <div
              key={name}
              className={origin.drift ? "adm-stat adm-stat--drift" : "adm-stat"}
            >
              <span className="adm-stat-label">{field.label}</span>
              <span className="adm-stat-value">
                {format(name, field.value)}
                {field.unit && name !== "max_upload_bytes" && (
                  <small> {field.unit}</small>
                )}
              </span>
              <span className="adm-stat-origin">{origin.text}</span>
            </div>
          );
        })}
    </section>
  );
}

function format(name: string, value: number): string {
  if (name === "max_upload_bytes") {
    return `${Math.round(value / (1024 * 1024))} MB`;
  }
  if (name === "min_score_ratio") {
    return value === 0 ? "sin filtrar" : `${Math.round(value * 100)} %`;
  }
  return String(value);
}

/**
 * What the header badge says about the current state.
 *
 * Three states, not two. «Personalizado» used to show whenever no level was
 * selected, which is also true of a tenant that has never opened this panel: the
 * badge claimed a customisation that did not exist, on a row that did not exist
 * either. It now only claims one when something is actually overridden.
 */
function badge(policy: Policy, level: number | null): string {
  if (level !== null) return `Nivel ${level}`;
  const numbers = Object.values(policy.fields).some((field) => field.overridden);
  const prompt = policy.prompt.source === "organizacion";
  return numbers || prompt ? "Personalizado" : "Valores del despliegue";
}

/**
 * The message for a guardrails failure, naming the control that caused it.
 *
 * Every 422 used to collapse to "algún valor está fuera del rango", which sends
 * the reader to look at the sliders — wrong for the prompt-length error, and
 * unhelpful for the rest, because the API says which field and what range it
 * accepts. `labels` maps a field name to the label the panel shows for it, so the
 * message points at something the reader can see on screen.
 */
function describe(error: unknown, labels: Record<string, string> = {}): string {
  if (!(error instanceof ApiError)) return "No se ha podido cargar la configuración.";
  if (error.status !== 422) return error.message;

  const field = typeof error.detail.field === "string" ? error.detail.field : null;
  if (field === null) return "Algún valor está fuera del rango que acepta el servidor.";

  const name = labels[field] ?? field;
  const low = error.detail.min;
  const high = error.detail.max;
  if (typeof low === "number" && typeof high === "number") {
    return `«${name}»: el servidor acepta entre ${low} y ${high}.`;
  }
  return `«${name}»: el valor no es válido.`;
}
